import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";

const API = API_BASE_URL;

const CORE_FIELDS = [
  { key: "id", label: "Lesson ID" },
  { key: "week", label: "Week" },
  { key: "lesson", label: "Lesson Number" },
  { key: "title", label: "Lesson Title" },
  { key: "objective", label: "Lesson Objective" },
  { key: "learning_objective", label: "Learning Objective" },
  { key: "task", label: "Student Writing Prompt" },
  { key: "task_base", label: "Prompt Base" },
  { key: "task_decoder", label: "Prompt Decoder" },
  { key: "constraints", label: "Writing Requirements" },
  { key: "microteach", label: "Teaching Point" },
  { key: "examples", label: "Examples" },
  { key: "rubric", label: "Evaluation Rules" },
  { key: "rubric_focus", label: "Rubric Focus" },
  { key: "success_criteria", label: "What Success Looks Like" },
  { key: "lesson_mode", label: "Lesson Mode" },
  { key: "evaluation_strategy", label: "Evaluation Strategy" },
  { key: "failure_modes", label: "Common Failure Modes" },
  { key: "extension", label: "Extension Activity" },
  { key: "teacher_notes", label: "Tutor Notes" },
  { key: "task_type", label: "Task Type" },
  { key: "min_word_count", label: "Minimum Word Count" },
  { key: "intro_video", label: "Intro Video" },
];

const OPTIONAL_FIELDS = [
  { key: "submission_sections", label: "Submission Sections" },
  { key: "before_draft", label: "Before Draft" },
  { key: "base_scene", label: "Base Scene" },
  { key: "before_version", label: "Before Version" },
  { key: "story_asset", label: "Story Asset" },
  { key: "curve_chart", label: "Curve Chart" },
  { key: "rewrite_challenge", label: "Rewrite Challenge" },
  { key: "target_word_count", label: "Target Word Count" },
  { key: "encouragement_policy", label: "Encouragement Policy" },
];

function isRenderable(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function formatPathLabel(path, fallback = "Item") {
  if (!path) return fallback;
  return String(path)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function renderValue(value, depth = 0) {
  if (value === undefined || value === null) return null;

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return <div style={styles.valueText}>{String(value)}</div>;
  }

  if (Array.isArray(value)) {
    return (
      <ul style={styles.list}>
        {value.map((item, index) => (
          <li key={`${depth}-${index}`} style={styles.listItem}>
            {typeof item === "object" && item !== null ? (
              renderValue(item, depth + 1)
            ) : (
              <span>{String(item)}</span>
            )}
          </li>
        ))}
      </ul>
    );
  }

  const entries = Object.entries(value);
  if (!entries.length) return <div style={styles.valueText}>Not defined in curriculum.json</div>;

  return (
    <div style={depth === 0 ? styles.objectBlock : styles.objectNestedBlock}>
      {entries.map(([key, nestedValue]) => {
        if (!isRenderable(nestedValue)) return null;
        return (
          <section key={key} style={styles.nestedSection}>
            <div style={styles.nestedLabel}>{formatPathLabel(key)}</div>
            {renderValue(nestedValue, depth + 1)}
          </section>
        );
      })}
    </div>
  );
}

function FieldCard({ label, value, missing = false }) {
  return (
    <section style={styles.fieldCard}>
      <div style={styles.fieldLabel}>{label}</div>
      {missing ? (
        <div style={styles.missingText}>Not defined in curriculum.json</div>
      ) : (
        renderValue(value)
      )}
    </section>
  );
}

function LessonCard({ lesson, onOpen }) {
  const lessonCode = `W${lesson.week}L${lesson.lesson}`;
  const mode = lesson?.lesson_mode?.mode || lesson?.lesson_mode || "";
  const taskType = lesson?.task_type || "";

  return (
    <button type="button" style={styles.lessonCard} onClick={onOpen}>
      <div style={styles.lessonCode}>{lessonCode}</div>
      <div style={styles.lessonTitle}>{lesson.title || `Lesson ${lesson.lesson}`}</div>
      {mode ? <div style={styles.lessonMeta}>{mode}</div> : null}
      {taskType ? <div style={styles.lessonMeta}>{taskType}</div> : null}
    </button>
  );
}

export default function TutorCurriculumExplorer({ studentId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadCurriculum() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API}/curriculum`, { cache: "no-store" });
        if (!res.ok) throw new Error(`curriculum fetch failed (${res.status})`);
        const json = await res.json();
        if (!cancelled) setCurriculum(json || null);
      } catch {
        if (!cancelled) {
          setCurriculum(null);
          setError("Unable to load curriculum.json");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCurriculum();

    return () => {
      cancelled = true;
    };
  }, []);

  const weeks = useMemo(() => (Array.isArray(curriculum?.weeks) ? curriculum.weeks : []), [curriculum]);
  const isTutorRoute = location.pathname.startsWith("/tutor/");
  const selectedLesson = useMemo(() => {
    const week = Number(params.weekId);
    const lesson = Number(params.lessonId);
    if (!week || !lesson) return null;
    for (const weekBlock of weeks) {
      if (Number(weekBlock?.week) !== week) continue;
      const lessonObj = Array.isArray(weekBlock?.lessons)
        ? weekBlock.lessons.find((item) => Number(item?.lesson) === lesson)
        : null;
      if (lessonObj) return lessonObj;
    }
    return { week, lesson };
  }, [params.lessonId, params.weekId, weeks]);

  const browserPath = isTutorRoute ? "/tutor/curriculum" : `/student/dashboard/${studentId}/curriculum`;
  const closeTarget = isTutorRoute ? "/tutor/dashboard_v2" : `/student/dashboard/${studentId}`;
  const openLesson = (week, lesson) => {
    navigate(
      isTutorRoute
        ? `/tutor/curriculum/week/${week}/lesson/${lesson}`
        : `/student/dashboard/${studentId}/curriculum/week/${week}/lesson/${lesson}`
    );
  };

  const goBackToBrowser = () => navigate(browserPath);

  if (loading) {
    return <div style={styles.loading}>Loading curriculum...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (selectedLesson && (params.weekId || params.lessonId)) {
    const lessonData = selectedLesson;
    return (
      <div style={styles.page}>
        <div style={styles.pageHeader}>
          <div>
            <div style={styles.title}>Creative English Curriculum</div>
            <div style={styles.subtitle}>4 Weeks • 16 Lessons</div>
          </div>
          <button
            type="button"
            style={styles.closeButton}
            onClick={goBackToBrowser}
            aria-label="Close inspector"
            title="Close inspector"
          >
            ×
          </button>
        </div>

        <div style={styles.inspectorGrid}>
          {CORE_FIELDS.map((field) => {
            const value = lessonData?.[field.key];
            return (
              <FieldCard
                key={field.key}
                label={field.label}
                value={value}
                missing={!isRenderable(value)}
              />
            );
          })}

          {OPTIONAL_FIELDS.map((field) => {
            const value = lessonData?.[field.key];
            if (!isRenderable(value)) return null;
            return <FieldCard key={field.key} label={field.label} value={value} />;
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <div style={styles.title}>Creative English Curriculum</div>
          <div style={styles.subtitle}>4 Weeks • 16 Lessons</div>
        </div>
        <button
          type="button"
          style={styles.closeButton}
          onClick={() => navigate(closeTarget)}
          aria-label="Close curriculum"
          title="Close curriculum"
        >
          ×
        </button>
      </div>

      <div style={styles.weekGrid}>
        {weeks.map((weekBlock) => (
          <section key={weekBlock.week} style={styles.weekCard}>
            <div style={styles.weekTitle}>Week {weekBlock.week}</div>
            <div style={styles.lessonList}>
              {(Array.isArray(weekBlock.lessons) ? weekBlock.lessons : []).map((lesson) => (
                <LessonCard
                  key={lesson.id || `W${weekBlock.week}L${lesson.lesson}`}
                  lesson={lesson}
                  onOpen={() => openLesson(weekBlock.week, lesson.lesson)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "24px 0 32px",
  },
  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
  },
  title: {
    fontSize: 30,
    fontWeight: 700,
    color: "var(--text)",
  },
  subtitle: {
    marginTop: 4,
    color: "var(--muted)",
  },
  weekGrid: {
    display: "grid",
    gap: 16,
  },
  weekCard: {
    background: "rgba(14,19,27,.86)",
    border: "1px solid #1a2230",
    borderRadius: 16,
    padding: 16,
  },
  weekTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 12,
    color: "var(--text)",
  },
  lessonList: {
    display: "grid",
    gap: 10,
  },
  lessonCard: {
    textAlign: "left",
    width: "100%",
    borderRadius: 12,
    border: "1px solid #243044",
    background: "#111827",
    padding: 14,
    cursor: "pointer",
    color: "var(--text)",
  },
  lessonCode: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#93c5fd",
    marginBottom: 6,
  },
  lessonTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 6,
  },
  lessonMeta: {
    fontSize: 12,
    color: "#cbd5e1",
    marginTop: 2,
  },
  loading: {
    padding: 24,
    color: "var(--muted)",
  },
  error: {
    padding: 16,
    borderRadius: 12,
    background: "#2a1111",
    color: "#fca5a5",
    border: "1px solid #5c1f1f",
  },
  inspectorGrid: {
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  },
  fieldCard: {
    background: "rgba(14,19,27,.86)",
    border: "1px solid #1a2230",
    borderRadius: 16,
    padding: 16,
  },
  fieldLabel: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#93c5fd",
    marginBottom: 10,
  },
  valueText: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    color: "var(--text)",
  },
  missingText: {
    color: "#fca5a5",
    lineHeight: 1.6,
  },
  list: {
    margin: 0,
    paddingInlineStart: 20,
    display: "grid",
    gap: 8,
  },
  listItem: {
    lineHeight: 1.6,
  },
  objectBlock: {
    display: "grid",
    gap: 12,
  },
  objectNestedBlock: {
    display: "grid",
    gap: 10,
    marginLeft: 12,
    paddingLeft: 12,
    borderLeft: "1px solid rgba(148,163,184,.18)",
  },
  nestedSection: {
    display: "grid",
    gap: 6,
  },
  nestedLabel: {
    fontSize: 12,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#c4b5fd",
  },
  backButton: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid rgba(148, 163, 184, 0.35)",
    background: "rgba(255, 255, 255, 0.08)",
    color: "#fff",
    cursor: "pointer",
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 999,
    border: "1px solid rgba(148, 163, 184, 0.35)",
    background: "rgba(255, 255, 255, 0.08)",
    color: "#fff",
    cursor: "pointer",
    fontSize: 22,
    lineHeight: 1,
  },
};
